import moment from 'moment-timezone'
import PropTypes from 'prop-types'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getLocale } from 'ggx-src/legacy/utils/locale'
import { _orderApi as transportAPI } from 'ggx-src/van-booking/api/order-api.service'
import { deliveryApi } from 'ggx-src/delivery/api/api.service'
import styled from "styled-components"
import { useSelector } from 'react-redux'
import { getFeatureFlag } from 'ggx-service/feature-flag/selectors'
import { SPACING } from "ggx-componentlibrary/design/spacing/spacing.constants"
import { FONT_REGULAR, FONT_SIZE, LINE_HEIGHT } from "ggx-componentlibrary/design/typography/typography.constants"
import {
  StyledControl,
  StyledDropdownIndicator,
  StyledPlaceholder,
} from "ggx-componentlibrary/components/dropdown/dropdown.component.styles"
import { COLOR } from "ggx-componentlibrary/design/color/color.constants"
import Dialog from '@mui/material/Dialog'
import { LoadingSpinner } from 'ggx-src/legacy/views/helpers/loading/spinner'
import { Body, SmallText } from "ggx-componentlibrary/design/typography/typography.styles"
import { displayAlert } from 'ggx-componentlibrary/components/alerts/alerts.component'
import BET_4157 from '../BET_4157'

// import { useEffect } from 'react'
// import { PDFViewer } from '@react-pdf/renderer'

const getTransportOrderRequestReportData = async (params = {}) => {
  const method = 'get'
  const endpoint = '/b2b/order_requests/report_data'
  const result = await transportAPI.request(
    method,
    endpoint,
    null,
    null,
    null,
    null,
    params
  )
  return result.data
}

const getDeliveryOrderRequestReportData = async (params = {}) => {
  const method = 'get'
  const endpoint = `/orders/report_data`
  const result = await deliveryApi.request(
    method,
    endpoint,
    null,
    null,
    null,
    null,
    params
  )
  return result?.data?.data
}

const DisabledStyleExteinsion = styled.div`
  position: relative;
  &[disabled] {
    cursor: not-allowed;
    ${StyledControl} {
      background-color: ${COLOR.GREY_LIGHTEST};
      border: 1px solid ${COLOR.GREY_LIGHT};
    }
    ${StyledPlaceholder} {
      color: ${COLOR.GREY_DARK};
    }
    ${StyledDropdownIndicator} {
      use {
        fill: ${COLOR.GREY_DARK};
      }
    }

    &:hover:before {
      z-index: 1;
      content: "${props => props['data-label']}";
      position: absolute;
      top: calc(100% + ${SPACING.XS});
      right: 0;
      background-color: ${COLOR.WHITE};
      ${FONT_REGULAR} 
      font-size: ${FONT_SIZE.SMALL};
      line-height: ${LINE_HEIGHT.SMALL};
      color: ${COLOR.BLACK};
      padding: ${SPACING.S};
      white-space: nowrap;
      border-radius: ${SPACING.XS};
      border: 1px solid rgba(0, 0, 0, 0.1);
      box-shadow: 0px 4px 10px 0px rgba(0, 18, 26, 0.09), 0px 8px 24px 0px rgba(0, 18, 26, 0.06), 0px 3px 30px 0px rgba(0, 18, 26, 0.05);
    }
  }
`

const generatePdfInWorker = (type, reportData) =>
  new Promise((resolve, reject) => {
    const worker = new Worker(new URL('./pdf.worker.js', import.meta.url))
    worker.onmessage = (e) => {
      worker.terminate()
      if (e.data.type === 'success') resolve(e.data.blob)
      else reject(new Error(e.data.message ?? 'PDF generation failed'))
    }
    worker.onerror = (err) => {
      worker.terminate()
      reject(new Error(err))
    }
    worker.postMessage({
      type,
      reportData,
      locale: getLocale(),
    })
  })

const downloadBlob = (blob, fileName) => {
  const fileURL = globalThis.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = fileURL
  link.download = fileName
  link.click()
  globalThis.URL.revokeObjectURL(fileURL)
}

function LocalGeneratingPDFReportProvider({
  type,
  totalCount,
  params,
  children,
}) {
  const [t] = useTranslation()
  const enableFEGenerateFiles = useSelector(state =>
    getFeatureFlag({ state, featureFlag: 'enable_fe_generate_files' })
  )
  const [isLoading, setIsLoading] = useState(false)
  const onChangeHOF = (originalFunc) => {
    return async ({ value }) => {
      if (isLoading) return
      if (!enableFEGenerateFiles)
        return originalFunc({ value })

      try {
        setIsLoading(true)
        if (value === 'pdf') {
          params.file_type = value
          if (type === 'transport') {
            await BET_4157.trackFinishedGeneratingReport(
              'GGXB_Order_Report_Generation_Result',
              async () => {
                const reportData = await getTransportOrderRequestReportData(params)
                downloadBlob(
                  await generatePdfInWorker('transport', reportData),
                  `${t('CSV-title__gogobusiness_report', {
                    companyName: reportData.meta.organization.name,
                    date: moment().format('YYYYMMDD'),
                  })}.pdf`
                )
              }
            )
            BET_4157.trackTransportDownloadReport(params, totalCount)
          } else {
            await BET_4157.trackFinishedGeneratingReport(
              'GGD_Order_Report_Generation_Result',
              async () => {
                const reportData = await getDeliveryOrderRequestReportData(params)
                downloadBlob(
                  await generatePdfInWorker('delivery', reportData),
                  `${moment(params.start_date).format('YYYYMMDD')}-${moment(params.end_date).format('YYYYMMDD')} GoGoVan Delivery orders.pdf`
                )
              }
            )
            BET_4157.trackDeliveryDownloadReport(params, totalCount)
          }
        } else {
          await originalFunc({ value })
        }
        setTimeout(() => displayAlert({ message: t('download_complete') }), 1000)
      } catch (error) {
        console.error(error)
        setTimeout(() => displayAlert({ type: 'error', message: t('error__downloading') }), 1000)
        throw error
      } finally {
        setIsLoading(false)
      }
    }
  }
  return (
    <>
      <DisabledStyleExteinsion>
        {children(enableFEGenerateFiles, onChangeHOF)}
      </DisabledStyleExteinsion>
      <Dialog
        disableEscapeKeyDown
        open={isLoading}
        sx={{
          '& .MuiPaper-root': {
            width: '232px',
            padding: SPACING.L,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          },
          '& *': {
            textAlign: 'center',
          }
        }}
      >
        <LoadingSpinner style={{ marginBottom: SPACING.L }} />
        <Body typographyStyles="mb-xxs">{t('downloading_report')}</Body>
        <SmallText typographyStyles="mb-none">{t('keep_window_open')}</SmallText>
      </Dialog>
    </>
  )
}

LocalGeneratingPDFReportProvider.propTypes = {
  type: PropTypes.string.isRequired,
  totalCount: PropTypes.number,
  params: PropTypes.object,
  children: PropTypes.func.isRequired,
}

export default {
  LocalGeneratingPDFReportProvider,
  // For debug purpose
  // PDFViewrForDeliveryReport({ startDate, endDate }) {
  //   const [reportData, setReportData] = useState(null)
  //   useEffect(() => {
  //     const fetchReportData = async () => {
  //       const reportData = await getDeliveryOrderRequestReportData({
  //         start_date: startDate.replace(/T.*$/g, '').replace(/-/g, '/'),
  //         end_date: endDate.replace(/T.*$/g, '').replace(/-/g, '/'),
  //       })
  //       setReportData(reportData)
  //     }
  //     fetchReportData()
  //   }, [startDate, endDate])
  //   return <PDFViewer style={{ zIndex: 1000, position: 'fixed', top: 0, left: 0, width: '100%', height: '100%' }}>
  //     {reportData && <DeliveryReport {...reportData} />}
  //   </PDFViewer>
  // },
  // For debug purpose
  // PDFViewrForTransportReport({ startDate, endDate }) {
  //   const [reportData, setReportData] = useState(null)
  //   useEffect(() => {
  //     const fetchReportData = async () => {
  //       const reportData = await getTransportOrderRequestReportData({
  //         start_date: startDate.replace(/T.*$/g, '').replace(/-/g, '/'),
  //         end_date: endDate.replace(/T.*$/g, '').replace(/-/g, '/'),
  //       })
  //       setReportData(reportData)
  //     }
  //     fetchReportData()
  //   }, [startDate, endDate])
  //   return <PDFViewer style={{ zIndex: 1000, position: 'fixed', top: 0, left: 0, width: '100%', height: '100%' }}>
  //     {reportData && <TransportReport {...reportData} />}
  //   </PDFViewer>
  // },
}
